import React, { useRef, useEffect, useCallback, useState } from 'react';
import { 
  Box, 
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatColorText,
  FormatColorFill,
  InsertLink,
  InsertEmoticon,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  MoreVert as MoreIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, readOnly = false }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const [colorMenuAnchor, setColorMenuAnchor] = useState<null | HTMLElement>(null);
  const [bgColorMenuAnchor, setBgColorMenuAnchor] = useState<null | HTMLElement>(null);
  const [emojiMenuAnchor, setEmojiMenuAnchor] = useState<null | HTMLElement>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [savedSelection, setSavedSelection] = useState<Range | null>(null);
  
  // Sincroniza el contenido del editor si el valor externo cambia.
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && value !== editor.innerHTML) {
      // console.log('[DEBUG] RichTextEditor - cargando contenido:', value);
      editor.innerHTML = value;
    }
  }, [value]);

  // Deshabilitar imágenes con esquema cid: para evitar errores de carga
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      // Usar setTimeout para asegurar que el contenido se haya cargado
      const timeoutId = setTimeout(() => {
        const images = editor.querySelectorAll('img[src^="cid:"]');
        images.forEach(img => {
          const imgElement = img as HTMLImageElement;
          imgElement.style.display = 'none';
          imgElement.setAttribute('data-cid-disabled', 'true');
          // También prevenir que se cargue la imagen
          imgElement.removeAttribute('src');
          imgElement.setAttribute('data-original-src', imgElement.getAttribute('src') || '');
        });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [value]);

  // Llama a la función onChange cuando el contenido del editor cambia.
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  /**
   * Función unificada para aplicar comandos de formato.
   * Prioriza la manipulación directa de estilos para mayor fiabilidad.
   */
  const applyCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    handleInput(); // Actualiza el estado después de cada comando
  };
  
  /**
   * Aplica estilos de bloque como la alineación del texto.
   * Modifica directamente el estilo del elemento de bloque padre.
   */
  const applyBlockStyle = (textAlign: 'left' | 'center' | 'right' | 'justify') => {
    const selection = window.getSelection();
    if (!selection?.anchorNode) return;
    
    let parentBlock = getParentBlock(selection.anchorNode);

    if (parentBlock) {
      parentBlock.style.textAlign = textAlign;
    } else {
      // Si no hay un bloque padre (texto plano), envolvemos la línea en un div y aplicamos el estilo
      applyCommand('formatBlock', '<div>');
      parentBlock = getParentBlock(selection.anchorNode);
      if (parentBlock) {
        parentBlock.style.textAlign = textAlign;
      }
    }
    handleInput();
  };

  /**
   * Encuentra el elemento de bloque (p, div, h*) más cercano a un nodo.
   */
  const getParentBlock = (node: Node | null): HTMLElement | null => {
    while (node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const display = window.getComputedStyle(element).display;
        if (display === 'block') {
          return element;
        }
      }
      node = node.parentNode;
    }
    return null;
  };

  // Maneja eventos de teclado como Enter y Tab
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      // Usar el comando nativo para insertar un salto de línea
      document.execCommand('insertLineBreak', false);
      handleInput();
    } else if (event.key === 'Tab') {
      event.preventDefault();
      applyCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
  };

  // Maneja clics en enlaces para hacerlos clickeables
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      event.preventDefault();
      const href = target.getAttribute('href');
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    }
  };

  // --- Manejadores para menús y diálogos (color, emoji, enlace) ---

  const handleColorClick = (color: string) => {
    applyCommand('foreColor', color);
    setColorMenuAnchor(null);
  };

  const handleBgColorClick = (color: string) => {
    applyCommand('hiliteColor', color);
    setBgColorMenuAnchor(null);
    setEmojiMenuAnchor(null);
  };

  const handleEmojiClick = (emoji: string) => {
    applyCommand('insertText', emoji);
    setEmojiMenuAnchor(null);
  };

  const handleLinkOpen = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      // Guardar la selección actual
      const range = selection.getRangeAt(0);
      setSavedSelection(range.cloneRange());
      setLinkText(selection.toString());
    } else {
      setSavedSelection(null);
      setLinkText('');
    }
    setLinkDialogOpen(true);
  };

  const handleLinkClose = () => {
    setLinkDialogOpen(false);
    setLinkUrl('');
    setLinkText('');
    setSavedSelection(null);
  };

  const handleLinkInsert = () => {
    if (linkUrl) {
      // Restaurar la selección guardada
      if (savedSelection) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(savedSelection);
        }
      }
      
      // Asegurar que la URL tenga protocolo
      let fullUrl = linkUrl.trim();
      if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://') && !fullUrl.startsWith('mailto:')) {
        fullUrl = 'https://' + fullUrl;
      }
      
      // Verificar si hay texto seleccionado
      const selection = window.getSelection();
      if (selection && selection.toString().trim()) {
        // Si hay texto seleccionado, crear el enlace con ese texto
        const selectedText = selection.toString();
        const linkHtml = `<a href="${fullUrl}" style="color: #1976d2; text-decoration: underline;" target="_blank">${selectedText}</a>`;
        applyCommand('insertHTML', linkHtml);
      } else {
        // Si no hay texto seleccionado, usar la URL como texto del enlace
        const linkHtml = `<a href="${fullUrl}" style="color: #1976d2; text-decoration: underline;" target="_blank">${fullUrl}</a>`;
        applyCommand('insertHTML', linkHtml);
      }
    }
    handleLinkClose();
  };
  
  const handleImageInsert = () => {
    // Abrir el diálogo de selección de archivo
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    setEmojiMenuAnchor(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Verificar que sea una imagen
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (result) {
            // Insertar la imagen como base64 (ya es redimensionable automáticamente)
            const imgHtml = `<img src="${result}" style="max-width: 100%; height: auto;" alt="${file.name}" />`;
            // Usar insertHTML en lugar de applyCommand para mejor control
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const div = document.createElement('div');
              div.innerHTML = imgHtml;
              range.deleteContents();
              range.insertNode(div.firstChild!);
              handleInput(); // Actualizar el estado
            } else {
              // Si no hay selección, insertar al final
              if (editorRef.current) {
                editorRef.current.innerHTML += imgHtml;
                handleInput(); // Actualizar el estado
              }
            }
          }
        };
        reader.readAsDataURL(file);
      } else {
        alert('Por favor selecciona un archivo de imagen válido.');
      }
    }
    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = '';
  };

  const clearFormatting = () => {
    applyCommand('removeFormat');
    setEmojiMenuAnchor(null);
  };

  const colors = ['#000000', '#FF0000', '#0000FF', '#008000', '#FFFF00', '#FFA500'];
  const emojis = ['😊', '😂', '❤️', '👍', '🤔', '🎉'];

  return (
    <Box sx={{ 
      border: '1px solid #ccc', 
      borderRadius: 1, 
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      {!readOnly && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            p: 0.5,
            bgcolor: 'grey.100',
            borderBottom: '1px solid #ccc',
            flexShrink: 0,
          }}
        >
          {/* Formato de texto */}
          <Tooltip title="Negrita">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand('bold')}>
              <FormatBold />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cursiva">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand('italic')}>
              <FormatItalic />
            </IconButton>
          </Tooltip>
          <Tooltip title="Subrayado">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand('underline')}>
              <FormatUnderlined />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

          {/* Listas */}
          <Tooltip title="Lista con viñetas">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand('insertUnorderedList')}>
              <FormatListBulleted />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lista numerada">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyCommand('insertOrderedList')}>
              <FormatListNumbered />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

          {/* Alineación */}
          <Tooltip title="Alinear a la izquierda">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyBlockStyle('left')}>
              <FormatAlignLeft />
            </IconButton>
          </Tooltip>
          <Tooltip title="Centrar">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyBlockStyle('center')}>
              <FormatAlignCenter />
            </IconButton>
          </Tooltip>
          <Tooltip title="Alinear a la derecha">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => applyBlockStyle('right')}>
              <FormatAlignRight />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

          {/* Opciones adicionales */}
          <Tooltip title="Color de texto">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={(e) => setColorMenuAnchor(e.currentTarget)}>
              <FormatColorText />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insertar enlace">
            <IconButton 
              size="small" 
              tabIndex={-1} 
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleLinkOpen();
              }}
            >
              <InsertLink />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Más opciones">
            <IconButton size="small" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={(e) => setEmojiMenuAnchor(e.currentTarget)}>
              <MoreIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* El área de edición de texto */}
      <Box
        component="div"
        ref={editorRef}
        contentEditable={!readOnly}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        lang="es"
        spellCheck={true}
        sx={{
          p: 1.5,
          flexGrow: 1,
          overflowY: 'auto',
          outline: 'none',
          cursor: readOnly ? 'default' : 'text',
          backgroundColor: 'white',
          '& a': {
            color: '#1976d2',
            textDecoration: 'underline',
            cursor: 'pointer',
          },
          '& a:hover': {
            color: '#1565c0',
            textDecoration: 'underline',
          },
          '& img': {
            maxWidth: '800px',
            height: 'auto',
            cursor: 'pointer',
            border: '2px solid transparent',
            transition: 'border-color 0.2s ease',
            resize: 'both',
            overflow: 'auto',
            minWidth: '50px',
            minHeight: '50px',
            maxHeight: '600px',
            '&:hover': {
              borderColor: '#1976d2',
            },
            '&:focus': {
              borderColor: '#1976d2',
              outline: 'none',
            },
            '&::-webkit-resizer': {
              borderWidth: '8px',
              borderStyle: 'solid',
              borderColor: '#1976d2 transparent transparent #1976d2',
              background: 'transparent',
            }
          }
        }}
      />
      
      {/* --- Menús y Diálogos --- */}
      <Menu
        anchorEl={colorMenuAnchor}
        open={Boolean(colorMenuAnchor)}
        onClose={() => setColorMenuAnchor(null)}
      >
        {colors.map((color) => (
          <MenuItem key={color} onClick={() => handleColorClick(color)}>
            <Box sx={{ width: 20, height: 20, bgcolor: color, border: '1px solid #ccc' }} />
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={emojiMenuAnchor}
        open={Boolean(emojiMenuAnchor)}
        onClose={() => setEmojiMenuAnchor(null)}
      >
        <MenuItem onClick={handleImageInsert}><ImageIcon sx={{mr:1}}/> Insertar Imagen...</MenuItem>
        <MenuItem onClick={() => setBgColorMenuAnchor(emojiMenuAnchor)}><FormatColorFill sx={{mr:1}}/> Color de fondo</MenuItem>
        <MenuItem onClick={clearFormatting}><FormatClear sx={{mr:1}}/> Limpiar formato</MenuItem>
      </Menu>

      <Menu
        anchorEl={bgColorMenuAnchor}
        open={Boolean(bgColorMenuAnchor)}
        onClose={() => setBgColorMenuAnchor(null)}
      >
        {colors.map((color) => (
          <MenuItem key={color} onClick={() => handleBgColorClick(color)}>
            <Box sx={{ width: 20, height: 20, bgcolor: color, border: '1px solid #ccc' }} />
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={linkDialogOpen} onClose={handleLinkClose} fullWidth maxWidth="sm" sx={{ zIndex: 9999 }}>
        <DialogTitle>Insertar Enlace</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL del enlace"
            placeholder="https://ejemplo.com o mailto:usuario@email.com"
            type="url"
            fullWidth
            variant="standard"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            helperText="Ingresa la URL completa (ej: https://google.com) o email (ej: mailto:usuario@email.com)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLinkClose}>Cancelar</Button>
          <Button onClick={handleLinkInsert}>Insertar</Button>
        </DialogActions>
      </Dialog>

      {/* Input oculto para selección de archivos */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </Box>
  );
};

export default RichTextEditor; 